import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Egg as EggIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CalendarMonth as CalendarIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useBirds, useBirdBlocks } from '../hooks/useBirds';
import {
  useBirdDayToDayProduction,
  useBirdProductionSummary,
  useUpdateEggProduction,
  useCreateEggProduction,
  useDeleteEggProduction,
} from '../hooks/useEggProduction';
import { useSearchParams } from 'react-router-dom';

const ACCENT = '#4caf50';

const BirdEggProductionPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const birdIdFromUrl = searchParams.get('birdId') || '';
  const [selectedBirdId, setSelectedBirdId] = useState<string>(birdIdFromUrl);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Record<string, any> | null>(null);
  const [editFormData, setEditFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    crate_quantity: 0,
    surplus_quantity: 0,
    broken_quantity: 0,
    remarks: '',
  });

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    block_id: '',
    crate_quantity: 0,
    surplus_quantity: 0,
    broken_quantity: 0,
    remarks: '',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState<Record<string, any> | null>(null);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkEditOrigDate, setBulkEditOrigDate] = useState('');
  const [bulkEditNewDate, setBulkEditNewDate] = useState('');
  const [bulkEditRows, setBulkEditRows] = useState<any[]>([]);
  const [bulkEditForm, setBulkEditForm] = useState<
    Record<number, { crate_quantity: number; surplus_quantity: number; broken_quantity: number }>
  >({});
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditError, setBulkEditError] = useState('');

  useEffect(() => {
    setSearchParams({ birdId: selectedBirdId, dateFrom, dateTo });
  }, [selectedBirdId, dateFrom, dateTo, setSearchParams]);

  const { data: birdsData, isLoading: birdsLoading, isFetching: birdsFetching } = useBirds();

  const {
    data: productionData,
    isLoading: productionLoading,
    isFetching: productionFetching,
    error,
    refetch,
  } = useBirdDayToDayProduction(
    selectedBirdId,
    dateFrom,
    dateTo,
    paginationModel.page + 1,
    paginationModel.pageSize
  );

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
  } = useBirdProductionSummary(selectedBirdId, dateFrom, dateTo);

  const updateMutation = useUpdateEggProduction();
  const createMutation = useCreateEggProduction();
  const deleteMutation = useDeleteEggProduction();

  const handleDeleteClick = (record: Record<string, any>) => {
    setDeletingRecord(record);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeletingRecord(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRecord) return;
    try {
      await deleteMutation.mutateAsync(deletingRecord.id);
      handleDeleteDialogClose();
      refetch();
    } catch (err) {
      console.error('Failed to delete production record:', err);
    }
  };

  const handleEditClick = (record: Record<string, any>) => {
    setEditingRecord(record);
    setEditFormData({
      date: record.date.split('T')[0],
      crate_quantity: record.crates,
      surplus_quantity: record.surplus,
      broken_quantity: record.broken_eggs,
      remarks: record.remarks || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingRecord(null);
    setEditFormData({
      date: new Date().toISOString().split('T')[0],
      crate_quantity: 0,
      surplus_quantity: 0,
      broken_quantity: 0,
      remarks: '',
    });
  };

  const handleUpdateSubmit = async () => {
    if (!editingRecord) return;
    try {
      await updateMutation.mutateAsync({ id: editingRecord.id, data: editFormData as any });
      handleEditDialogClose();
      refetch();
    } catch (err) {
      console.error('Failed to update production record:', err);
    }
  };

  const handleAddClick = () => {
    setAddFormData({
      date: new Date().toISOString().split('T')[0],
      block_id: '',
      crate_quantity: 0,
      surplus_quantity: 0,
      broken_quantity: 0,
      remarks: '',
    });
    setAddDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
    setAddFormData({
      date: new Date().toISOString().split('T')[0],
      block_id: '',
      crate_quantity: 0,
      surplus_quantity: 0,
      broken_quantity: 0,
      remarks: '',
    });
  };

  const handleAddSubmit = async () => {
    if (!selectedBirdId) return;
    try {
      await createMutation.mutateAsync({
        production_date: addFormData.date,
        crate_quantity: addFormData.crate_quantity,
        surplus_quantity: addFormData.surplus_quantity,
        broken_quantity: addFormData.broken_quantity,
        remarks: addFormData.remarks,
        birds_id: Number(selectedBirdId),
        ...(addFormData.block_id ? { block_id: Number(addFormData.block_id) } : {}),
      } as any);
      handleAddDialogClose();
      refetch();
    } catch (err) {
      console.error('Failed to create production record:', err);
    }
  };

  const handleBulkEdit = (date: string, rows: any[]) => {
    setBulkEditOrigDate(date);
    setBulkEditNewDate(date);
    setBulkEditRows(rows);
    const form: Record<
      number,
      { crate_quantity: number; surplus_quantity: number; broken_quantity: number }
    > = {};
    rows.forEach((row) => {
      form[row.id] = {
        crate_quantity: row.crates ?? 0,
        surplus_quantity: row.surplus ?? 0,
        broken_quantity: row.broken_eggs ?? 0,
      };
    });
    setBulkEditForm(form);
    setBulkEditError('');
    setBulkEditOpen(true);
  };

  const handleBulkEditClose = () => {
    setBulkEditOpen(false);
    setBulkEditError('');
  };

  const handleBulkEditSubmit = async () => {
    setBulkEditSaving(true);
    setBulkEditError('');
    try {
      for (const row of bulkEditRows) {
        const f = bulkEditForm[row.id];
        await updateMutation.mutateAsync({
          id: row.id,
          data: {
            date: bulkEditNewDate,
            crate_quantity: f.crate_quantity,
            surplus_quantity: f.surplus_quantity,
            broken_quantity: f.broken_quantity,
          } as any,
        });
      }
      setBulkEditOpen(false);
      refetch();
    } catch (e: any) {
      setBulkEditError(e?.response?.data?.message || 'Failed to save changes');
    } finally {
      setBulkEditSaving(false);
    }
  };

  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const layingBirds = (birdsData?.data ?? []).filter((b: any) => b.is_laying);
  const selectedBird = layingBirds.find((b: any) => b.id === Number(selectedBirdId)) ?? null;
  const isCaged = !!(selectedBird as any)?.cage_date;
  const { data: birdBlocks = [] } = useBirdBlocks(Number(selectedBirdId));
  const cageBlocks = (birdBlocks as any[]).filter((b) => (b.type ?? 'rearing') === 'cage');

  // Group production rows by date for the caged birds view
  const groupedByDate = React.useMemo(() => {
    const rows: any[] = productionData?.data ?? [];
    const map = new Map<string, any[]>();
    for (const row of rows) {
      const key = row.date;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    }
    return map;
  }, [productionData?.data]);

  const totalBirdCount = (selectedBird as any)?.current_quantity ?? 0;

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      flex: 1.2,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ color: '#fff', lineHeight: 1.2 }}>
            {new Date(params.value).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
          >
            {params.row.day_of_week}
          </Typography>
        </Box>
      ),
    },
    ...(isCaged
      ? [
          {
            field: 'block_name',
            headerName: 'Room',
            flex: 1,
            minWidth: 120,
            renderCell: (params: GridRenderCellParams) => (
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}
              >
                {params.value ?? '—'}
              </Typography>
            ),
          } as GridColDef,
        ]
      : []),
    {
      field: 'eggs',
      headerName: 'Eggs Collected',
      flex: 1.3,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ color: '#ff9800', lineHeight: 1.2 }}>
            {params.row.crates} crates + {params.row.surplus}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
          >
            {params.row.total_eggs?.toLocaleString()} eggs
          </Typography>
        </Box>
      ),
    },
    {
      field: 'lay_percentage',
      headerName: 'Lay %',
      flex: 1,
      minWidth: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const pct = params.value;
        const color = pct >= 80 ? '#4caf50' : pct >= 60 ? '#ff9800' : '#f44336';
        return (
          <Typography variant="body2" fontWeight={700} sx={{ color }}>
            {pct?.toFixed(1)}%
          </Typography>
        );
      },
    },
    {
      field: 'broken_eggs',
      headerName: 'Broken',
      flex: 0.7,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{
            color: params.value > 0 ? '#ffa726' : 'rgba(255,255,255,0.3)',
            fontWeight: params.value > 0 ? 600 : 400,
          }}
        >
          {params.value || 0}
        </Typography>
      ),
    },
    {
      field: 'day_over_day_diff',
      headerName: 'Day Change',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params: GridRenderCellParams) => {
        const diff = params.row.day_over_day_diff;
        if (diff === null || diff === undefined) {
          return (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
              —
            </Typography>
          );
        }
        const color = diff > 0 ? '#4caf50' : diff < 0 ? '#f44336' : '#ff9800';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {diff > 0 ? (
              <ArrowUpwardIcon sx={{ fontSize: 14, color }} />
            ) : diff < 0 ? (
              <ArrowDownwardIcon sx={{ fontSize: 14, color }} />
            ) : (
              <RemoveIcon sx={{ fontSize: 14, color }} />
            )}
            <Typography variant="body2" fontWeight={600} sx={{ color }}>
              {diff > 0 ? `+${diff.toLocaleString()}` : diff.toLocaleString()}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'day_over_day_pct',
      headerName: 'Change %',
      flex: 0.9,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const pct = params.row.day_over_day_pct;
        if (pct === null || pct === undefined) {
          return (
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
              —
            </Typography>
          );
        }
        const color = pct > 0 ? '#4caf50' : pct < 0 ? '#f44336' : '#ff9800';
        return (
          <Typography variant="body2" fontWeight={600} sx={{ color }}>
            {pct > 0 ? `+${pct}%` : `${pct}%`}
          </Typography>
        );
      },
    },
    {
      field: 'remarks',
      headerName: 'Remarks',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.4)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => handleEditClick(params.row)}
            sx={{
              color: 'rgba(255,152,0,0.7)',
              '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
            }}
          >
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            sx={{
              color: 'rgba(244,67,54,0.6)',
              '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
            }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', p: 2.5 }}>
      {(productionFetching || summaryFetching || birdsFetching) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg,${ACCENT},#66bb6a)` },
          }}
        />
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 2 }}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
            Bird Production Records
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            {selectedBird ? (
              <>
                Viewing{' '}
                <span style={{ color: '#66bb6a', fontWeight: 600 }}>{selectedBird.name}</span>
              </>
            ) : (
              'Day-to-day egg production per batch'
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={!selectedBirdId}
          sx={{
            background: `linear-gradient(135deg,${ACCENT},#66bb6a)`,
            color: '#fff',
            px: 2,
            '&:hover': { background: 'linear-gradient(135deg,#388e3c,#4caf50)' },
            '&:disabled': { background: 'rgba(76,175,80,0.2)', color: 'rgba(255,255,255,0.3)' },
          }}
        >
          Add Record
        </Button>
      </Box>

      {/* Stats strip */}
      {selectedBirdId && summaryData && !summaryLoading && (
        <Paper
          sx={{
            mb: 2,
            px: 0,
            py: 0,
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            border: `1px solid rgba(76,175,80,0.12)`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
            {[
              {
                label: 'Total Eggs',
                value: summaryData.total_eggs?.toLocaleString() ?? '0',
                sub: `${summaryData.total_crates ?? 0} crates + ${summaryData.total_surplus ?? 0}`,
                icon: <EggIcon sx={{ fontSize: 15 }} />,
                color: '#ff9800',
              },
              {
                label: 'Avg Lay %',
                value: `${summaryData.average_lay_percentage?.toFixed(1) ?? '0'}%`,
                sub: `consistency ${summaryData.consistency_score?.toFixed(1) ?? '0'}%`,
                icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,
                color: '#66bb6a',
              },
              {
                label: 'Best Day',
                value: `${summaryData.best_day?.lay_percentage?.toFixed(1) ?? '0'}%`,
                sub: summaryData.best_day?.date
                  ? new Date(summaryData.best_day.date).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                    })
                  : '—',
                icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,
                color: '#42a5f5',
              },
              {
                label: 'Worst Day',
                value: `${summaryData.worst_day?.lay_percentage?.toFixed(1) ?? '0'}%`,
                sub: summaryData.worst_day?.date
                  ? new Date(summaryData.worst_day.date).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                    })
                  : '—',
                icon: <TrendingDownIcon sx={{ fontSize: 15 }} />,
                color: '#ef5350',
              },
              {
                label: 'Period',
                value: `${summaryData.total_records ?? 0} days`,
                sub: summaryData.date_range?.earliest
                  ? `${new Date(summaryData.date_range.earliest).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${new Date(summaryData.date_range.latest).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`
                  : '—',
                icon: <CalendarIcon sx={{ fontSize: 15 }} />,
                color: '#ab47bc',
              },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <Box
                  sx={{
                    flex: 1,
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '8px',
                      bgcolor: `${s.color}18`,
                      color: s.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', display: 'block' }}
                    >
                      {s.label.toUpperCase()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                      <Typography
                        fontWeight={700}
                        sx={{ color: s.color, lineHeight: 1.1, fontSize: '1rem' }}
                      >
                        {s.value}
                      </Typography>
                      {s.sub && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.68rem' }}
                        >
                          {s.sub}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
                {i < arr.length - 1 && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: 'rgba(76,175,80,0.08)' }}
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </Paper>
      )}

      {/* Table */}
      <Paper
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          border: `1px solid rgba(76,175,80,0.15)`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Toolbar / filters */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            borderBottom: `1px solid rgba(76,175,80,0.1)`,
          }}
        >
          <Autocomplete
            options={layingBirds}
            getOptionLabel={(b: any) => b.name}
            value={selectedBird}
            onChange={(_e, val: any) => setSelectedBirdId(val ? String(val.id) : '')}
            loading={birdsLoading}
            size="small"
            sx={{
              width: 220,
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: '0.8rem',
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(76,175,80,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
              '& input': { color: '#fff', py: '0px !important' },
              '& input::placeholder': { color: 'rgba(255,255,255,0.3)', opacity: 1 },
              '& .MuiAutocomplete-popupIndicator': { color: 'rgba(255,255,255,0.4)' },
              '& .MuiAutocomplete-clearIndicator': { color: 'rgba(255,255,255,0.4)' },
            }}
            renderInput={(params) => <TextField {...params} placeholder="Select bird batch…" />}
          />
          <TextField
            size="small"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
                    >
                      From
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 160,
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: '0.8rem',
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(76,175,80,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
              '& input': { color: '#fff', py: 0.5, colorScheme: 'dark' },
            }}
          />
          <TextField
            size="small"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
                    >
                      To
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              width: 160,
              '& .MuiOutlinedInput-root': {
                height: 32,
                fontSize: '0.8rem',
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(76,175,80,0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(76,175,80,0.4)' },
                '&.Mui-focused fieldset': { borderColor: ACCENT },
              },
              '& input': { color: '#fff', py: 0.5, colorScheme: 'dark' },
            }}
          />
          <Box flex={1} />
          {selectedBirdId && (
            <Chip
              label={productionData?.pagination?.total ?? 0}
              size="small"
              sx={{
                bgcolor: 'rgba(76,175,80,0.1)',
                color: ACCENT,
                fontWeight: 700,
                height: 18,
                fontSize: '0.7rem',
              }}
            />
          )}
        </Box>

        {!selectedBirdId ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              opacity: 0.4,
            }}
          >
            <EggIcon sx={{ fontSize: 48, color: '#66bb6a' }} />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Select a bird batch to view production records
            </Typography>
          </Box>
        ) : isCaged ? (
          /* Grouped view for caged birds */
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {/* Column header */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.4fr 1.5fr 1fr 0.8fr 1fr 1fr 90px',
                px: 2,
                py: 1,
                bgcolor: '#181e18',
                borderBottom: '1px solid rgba(76,175,80,0.15)',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              {['Date', 'Eggs Collected', 'Lay %', 'Broken', 'Day Change', 'Change %', ''].map(
                (h) => (
                  <Typography
                    key={h}
                    variant="caption"
                    sx={{
                      color: 'rgba(76,175,80,0.7)',
                      fontWeight: 700,
                      fontSize: '0.68rem',
                      letterSpacing: '0.7px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {h}
                  </Typography>
                )
              )}
            </Box>

            {productionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: ACCENT }} />
              </Box>
            ) : groupedByDate.size === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, opacity: 0.4 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  No records found
                </Typography>
              </Box>
            ) : (
              Array.from(groupedByDate.entries()).map(([date, rows]) => {
                const totalEggs = rows.reduce((s: number, r: any) => s + (r.total_eggs ?? 0), 0);
                const totalBroken = rows.reduce((s: number, r: any) => s + (r.broken_eggs ?? 0), 0);
                const layPct = totalBirdCount > 0 ? (totalEggs / totalBirdCount) * 100 : 0;
                const expanded = expandedDates.has(date);
                const firstRow = rows[0];

                return (
                  <Box key={date}>
                    {/* Summary row */}
                    <Box
                      onClick={() => toggleDate(date)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1.4fr 1.5fr 1fr 0.8fr 1fr 1fr 90px',
                        px: 2,
                        py: 1.25,
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        bgcolor: expanded ? 'rgba(76,175,80,0.06)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(76,175,80,0.07)' },
                        alignItems: 'center',
                      }}
                    >
                      {/* Date */}
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: '#fff', lineHeight: 1.2 }}
                        >
                          {new Date(date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
                        >
                          {firstRow?.day_of_week}
                        </Typography>
                      </Box>
                      {/* Total eggs */}
                      <Box>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ color: '#ff9800', lineHeight: 1.2 }}
                        >
                          {Math.floor(totalEggs / 30)} crates + {totalEggs % 30}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.65rem' }}
                        >
                          {totalEggs.toLocaleString()} eggs · {rows.length} rooms
                        </Typography>
                      </Box>
                      {/* Lay % */}
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{
                          color: layPct >= 80 ? '#4caf50' : layPct >= 60 ? '#ff9800' : '#f44336',
                        }}
                      >
                        {layPct.toFixed(1)}%
                      </Typography>
                      {/* Broken */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: totalBroken > 0 ? '#ffa726' : 'rgba(255,255,255,0.3)',
                          fontWeight: totalBroken > 0 ? 600 : 400,
                        }}
                      >
                        {totalBroken || 0}
                      </Typography>
                      {/* Day change — blank on summary */}
                      <Box />
                      <Box />
                      {/* Edit + expand */}
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Tooltip title="Edit all rooms for this date">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBulkEdit(date, rows);
                            }}
                            sx={{
                              color: 'rgba(255,152,0,0.7)',
                              '&:hover': { color: '#ff9800', bgcolor: 'rgba(255,152,0,0.1)' },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDate(date);
                          }}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            cursor: 'pointer',
                          }}
                        >
                          <Chip
                            label={`${rows.length} rooms`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: 'rgba(76,175,80,0.12)',
                              color: ACCENT,
                              fontWeight: 600,
                            }}
                          />
                          {expanded ? (
                            <ExpandLessIcon sx={{ fontSize: 18, color: ACCENT }} />
                          ) : (
                            <ExpandMoreIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.3)' }} />
                          )}
                        </Box>
                      </Box>
                    </Box>

                    {/* Per-room detail rows */}
                    <Collapse in={expanded}>
                      {rows.map((row: any) => {
                        const diff = row.day_over_day_diff;
                        const pct = row.day_over_day_pct;
                        const diffColor = diff > 0 ? '#4caf50' : diff < 0 ? '#f44336' : '#ff9800';
                        const rpct = row.lay_percentage;
                        const rcolor = rpct >= 80 ? '#4caf50' : rpct >= 60 ? '#ff9800' : '#f44336';
                        return (
                          <Box
                            key={row.id}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1.4fr 1.5fr 1fr 0.8fr 1fr 1fr 90px',
                              px: 2,
                              py: 1,
                              borderBottom: '1px solid rgba(255,255,255,0.03)',
                              bgcolor: 'rgba(0,0,0,0.18)',
                              alignItems: 'center',
                              '&:hover': { bgcolor: 'rgba(76,175,80,0.04)' },
                            }}
                          >
                            {/* Indented room name + bird count */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1.5 }}>
                              <Box
                                sx={{
                                  width: 3,
                                  height: 3,
                                  borderRadius: '50%',
                                  bgcolor: 'rgba(255,255,255,0.2)',
                                  flexShrink: 0,
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: 'rgba(255,255,255,0.55)',
                                    fontSize: '0.78rem',
                                    display: 'block',
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {row.block_name ?? '—'}
                                </Typography>
                                {(() => {
                                  const block = cageBlocks.find((b: any) => b.id === row.block_id);
                                  return block?.current_quantity ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'rgba(255,255,255,0.25)',
                                        fontSize: '0.65rem',
                                        lineHeight: 1,
                                      }}
                                    >
                                      {block.current_quantity.toLocaleString()} birds
                                    </Typography>
                                  ) : null;
                                })()}
                              </Box>
                            </Box>
                            {/* Eggs */}
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ color: 'rgba(255,152,0,0.75)', lineHeight: 1.2 }}
                              >
                                {row.crates} crates + {row.surplus}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.65rem' }}
                              >
                                {row.total_eggs?.toLocaleString()} eggs
                              </Typography>
                            </Box>
                            {/* Room lay % */}
                            <Typography variant="body2" fontWeight={600} sx={{ color: rcolor }}>
                              {rpct?.toFixed(1)}%
                            </Typography>
                            {/* Broken */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: row.broken_eggs > 0 ? '#ffa726' : 'rgba(255,255,255,0.25)',
                              }}
                            >
                              {row.broken_eggs || 0}
                            </Typography>
                            {/* Day change */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {diff == null ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(255,255,255,0.2)' }}
                                >
                                  —
                                </Typography>
                              ) : (
                                <>
                                  {diff > 0 ? (
                                    <ArrowUpwardIcon sx={{ fontSize: 12, color: diffColor }} />
                                  ) : diff < 0 ? (
                                    <ArrowDownwardIcon sx={{ fontSize: 12, color: diffColor }} />
                                  ) : (
                                    <RemoveIcon sx={{ fontSize: 12, color: diffColor }} />
                                  )}
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    sx={{ color: diffColor }}
                                  >
                                    {diff > 0 ? `+${diff}` : diff}
                                  </Typography>
                                </>
                              )}
                            </Box>
                            {/* Change % */}
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{
                                color:
                                  pct == null
                                    ? 'rgba(255,255,255,0.2)'
                                    : pct > 0
                                      ? '#4caf50'
                                      : pct < 0
                                        ? '#f44336'
                                        : '#ff9800',
                              }}
                            >
                              {pct == null ? '—' : pct > 0 ? `+${pct}%` : `${pct}%`}
                            </Typography>
                            {/* Actions — delete only; edit is on the summary row */}
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteClick(row);
                                }}
                                sx={{
                                  color: 'rgba(244,67,54,0.6)',
                                  '&:hover': { color: '#f44336', bgcolor: 'rgba(244,67,54,0.1)' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Collapse>
                  </Box>
                );
              })
            )}
            {/* Pagination for grouped view */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                px: 2,
                py: 1,
                borderTop: '1px solid rgba(76,175,80,0.12)',
                bgcolor: 'rgba(76,175,80,0.03)',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}
              >
                {productionData?.pagination?.total ?? 0} days
              </Typography>
              <Button
                size="small"
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel((p) => ({ ...p, page: p.page - 1 }))}
                sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 32, fontSize: '0.75rem' }}
              >
                Prev
              </Button>
              <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}
              >
                Page {paginationModel.page + 1}
              </Typography>
              <Button
                size="small"
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  (productionData?.pagination?.total ?? 0)
                }
                onClick={() => setPaginationModel((p) => ({ ...p, page: p.page + 1 }))}
                sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 32, fontSize: '0.75rem' }}
              >
                Next
              </Button>
            </Box>
          </Box>
        ) : (
          <DataGrid
            rows={productionData?.data ?? []}
            columns={columns}
            rowCount={productionData?.pagination?.total || 0}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            rowHeight={44}
            loading={productionLoading}
            sx={{
              flex: 1,
              border: 'none',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                color: '#fff',
                fontSize: '0.82rem',
              },
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'rgba(76,175,80,0.07)',
                borderBottom: 'rgba(76,175,80,0.15)',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '0.68rem',
                letterSpacing: '0.7px',
                color: 'rgba(76,175,80,0.7)',
                textTransform: 'uppercase',
              },
              '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(76,175,80,0.05)' },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid rgba(76,175,80,0.12)',
                bgcolor: 'rgba(76,175,80,0.03)',
                minHeight: 40,
              },
              '& .MuiTablePagination-root': { color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem' },
              '& .MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.4)' },
            }}
          />
        )}
      </Paper>

      {error && (
        <Typography sx={{ color: '#f44336', mt: 1, fontSize: '0.8rem' }}>
          {error instanceof Error ? error.message : 'Failed to load production data'}
        </Typography>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: '1px solid rgba(255,152,0,0.25)',
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: '1px solid rgba(255,152,0,0.15)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#ff9800',
          }}
        >
          Edit Production Record
          {editingRecord && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'rgba(255,255,255,0.4)', mt: 0.25 }}
            >
              {selectedBird?.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }} mt={0.5}>
              <TextField
                label="Production Date"
                type="date"
                fullWidth
                size="small"
                value={editFormData.date}
                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Crates"
                type="number"
                fullWidth
                size="small"
                value={editFormData.crate_quantity}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, crate_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0 } }}
                helperText="30 eggs per crate"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Surplus"
                type="number"
                fullWidth
                size="small"
                value={editFormData.surplus_quantity}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, surplus_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0, max: 29 } }}
                helperText="Extra eggs (0–29)"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert
                severity="success"
                sx={{
                  py: 0.5,
                  bgcolor: 'rgba(76,175,80,0.08)',
                  border: '1px solid rgba(76,175,80,0.2)',
                  '& .MuiAlert-icon': { color: '#66bb6a' },
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2" sx={{ color: '#66bb6a' }}>
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#4caf50' }}>
                    {(
                      editFormData.crate_quantity * 30 +
                      editFormData.surplus_quantity
                    ).toLocaleString()}{' '}
                    eggs
                  </Typography>
                </Box>
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Broken Eggs"
                type="number"
                fullWidth
                size="small"
                value={editFormData.broken_quantity}
                onChange={(e) =>
                  setEditFormData({ ...editFormData, broken_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Remarks"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={editFormData.remarks}
                onChange={(e) => setEditFormData({ ...editFormData, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid rgba(255,152,0,0.12)' }}>
          <Button
            size="small"
            onClick={handleEditDialogClose}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleUpdateSubmit}
            disabled={updateMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg,#ff9800,#ffa726)',
              color: '#fff',
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
              '&:disabled': { background: 'rgba(255,152,0,0.25)' },
            }}
          >
            {updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleAddDialogClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: '1px solid rgba(76,175,80,0.25)',
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: '1px solid rgba(76,175,80,0.15)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#66bb6a',
          }}
        >
          Add Production Record
          {selectedBirdId && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'rgba(255,255,255,0.4)', mt: 0.25 }}
            >
              {selectedBird?.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            {isCaged && (
              <Grid size={{ xs: 12 }} mt={0.5}>
                <Autocomplete
                  options={cageBlocks}
                  getOptionLabel={(b: any) => b.name}
                  value={cageBlocks.find((b: any) => String(b.id) === addFormData.block_id) ?? null}
                  onChange={(_e, val: any) =>
                    setAddFormData({ ...addFormData, block_id: val ? String(val.id) : '' })
                  }
                  size="small"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cage Room *"
                      required
                      helperText={`${cageBlocks.length} cage block${cageBlocks.length !== 1 ? 's' : ''} available`}
                    />
                  )}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }} mt={0.5}>
              <TextField
                label="Production Date"
                type="date"
                fullWidth
                size="small"
                value={addFormData.date}
                onChange={(e) => setAddFormData({ ...addFormData, date: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Crates"
                type="number"
                fullWidth
                size="small"
                value={addFormData.crate_quantity}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, crate_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0 } }}
                helperText="30 eggs per crate"
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                label="Surplus"
                type="number"
                fullWidth
                size="small"
                value={addFormData.surplus_quantity}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, surplus_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0, max: 29 } }}
                helperText="Extra eggs (0–29)"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Alert
                severity="success"
                sx={{
                  py: 0.5,
                  bgcolor: 'rgba(76,175,80,0.08)',
                  border: '1px solid rgba(76,175,80,0.2)',
                  '& .MuiAlert-icon': { color: '#66bb6a' },
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography variant="body2" sx={{ color: '#66bb6a' }}>
                    Total
                  </Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#4caf50' }}>
                    {(
                      addFormData.crate_quantity * 30 +
                      addFormData.surplus_quantity
                    ).toLocaleString()}{' '}
                    eggs
                  </Typography>
                </Box>
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Broken Eggs"
                type="number"
                fullWidth
                size="small"
                value={addFormData.broken_quantity}
                onChange={(e) =>
                  setAddFormData({ ...addFormData, broken_quantity: Number(e.target.value) })
                }
                slotProps={{ htmlInput: { min: 0 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Remarks"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={addFormData.remarks}
                onChange={(e) => setAddFormData({ ...addFormData, remarks: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid rgba(76,175,80,0.12)' }}>
          <Button
            size="small"
            onClick={handleAddDialogClose}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleAddSubmit}
            disabled={createMutation.isPending}
            sx={{
              background: `linear-gradient(135deg,${ACCENT},#66bb6a)`,
              color: '#fff',
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg,#388e3c,#4caf50)' },
              '&:disabled': { background: 'rgba(76,175,80,0.25)' },
            }}
          >
            {createMutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              'Add Record'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog — date + all cage rooms */}
      <Dialog
        open={bulkEditOpen}
        onClose={handleBulkEditClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: '1px solid rgba(255,152,0,0.25)',
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: '1px solid rgba(255,152,0,0.15)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#ff9800',
          }}
        >
          Edit Production — {bulkEditRows.length} rooms
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'rgba(255,255,255,0.4)', mt: 0.25 }}
          >
            {selectedBird?.name} ·{' '}
            {bulkEditOrigDate
              ? new Date(bulkEditOrigDate).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {bulkEditError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkEditError}
            </Alert>
          )}

          {/* Shared date */}
          <TextField
            label="Production Date"
            type="date"
            fullWidth
            size="small"
            value={bulkEditNewDate}
            onChange={(e) => setBulkEditNewDate(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ mb: 2.5 }}
          />

          {/* Per-room rows */}
          {bulkEditRows.map((row, idx) => {
            const f = bulkEditForm[row.id] ?? {
              crate_quantity: 0,
              surplus_quantity: 0,
              broken_quantity: 0,
            };
            const roomTotal = f.crate_quantity * 30 + f.surplus_quantity;
            const block = cageBlocks.find((b: any) => b.id === row.block_id);
            const blockBirds = block?.current_quantity ?? 0;
            const roomLayPct =
              blockBirds > 0 && roomTotal > 0 ? (roomTotal / blockBirds) * 100 : null;
            const update = (field: string, val: number) =>
              setBulkEditForm((prev) => ({
                ...prev,
                [row.id]: { ...prev[row.id], [field]: val },
              }));

            return (
              <Box key={row.id}>
                {idx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: ACCENT,
                      opacity: 0.6,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: 'rgba(255,255,255,0.8)', lineHeight: 1.2 }}
                    >
                      {row.block_name ?? `Room ${idx + 1}`}
                    </Typography>
                    {blockBirds > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' }}
                      >
                        {blockBirds.toLocaleString()} birds
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: roomTotal > 0 ? '#ff9800' : 'rgba(255,255,255,0.25)',
                        display: 'block',
                        lineHeight: 1.2,
                      }}
                    >
                      {roomTotal.toLocaleString()} eggs
                    </Typography>
                    {roomLayPct !== null && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.65rem',
                          color:
                            roomLayPct >= 80 ? '#4caf50' : roomLayPct >= 60 ? '#ff9800' : '#f44336',
                        }}
                      >
                        {roomLayPct.toFixed(1)}% lay
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Crates"
                      type="number"
                      size="small"
                      fullWidth
                      value={f.crate_quantity}
                      onChange={(e) => update('crate_quantity', Number(e.target.value))}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Surplus"
                      type="number"
                      size="small"
                      fullWidth
                      value={f.surplus_quantity}
                      onChange={(e) => update('surplus_quantity', Number(e.target.value))}
                      slotProps={{ htmlInput: { min: 0, max: 29 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Broken"
                      type="number"
                      size="small"
                      fullWidth
                      value={f.broken_quantity}
                      onChange={(e) => update('broken_quantity', Number(e.target.value))}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                </Grid>
              </Box>
            );
          })}

          {/* Batch total */}
          {bulkEditRows.length > 0 &&
            (() => {
              const grandTotal = bulkEditRows.reduce((s, row) => {
                const f = bulkEditForm[row.id];
                return s + (f ? f.crate_quantity * 30 + f.surplus_quantity : 0);
              }, 0);
              return (
                <Alert
                  severity="success"
                  sx={{
                    py: 0.5,
                    mt: 0.5,
                    bgcolor: 'rgba(76,175,80,0.08)',
                    border: '1px solid rgba(76,175,80,0.2)',
                    '& .MuiAlert-icon': { color: '#66bb6a' },
                  }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography variant="body2" sx={{ color: '#66bb6a' }}>
                      Batch total
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#4caf50' }}>
                      {Math.floor(grandTotal / 30)} crates + {grandTotal % 30} &nbsp;·&nbsp;{' '}
                      {grandTotal.toLocaleString()} eggs
                      {totalBirdCount > 0 && (
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>
                          {' '}
                          ({((grandTotal / totalBirdCount) * 100).toFixed(1)}% lay)
                        </span>
                      )}
                    </Typography>
                  </Box>
                </Alert>
              );
            })()}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid rgba(255,152,0,0.12)' }}>
          <Button
            size="small"
            onClick={handleBulkEditClose}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleBulkEditSubmit}
            disabled={bulkEditSaving}
            sx={{
              background: 'linear-gradient(135deg,#ff9800,#ffa726)',
              color: '#fff',
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
              '&:disabled': { background: 'rgba(255,152,0,0.25)' },
            }}
          >
            {bulkEditSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              `Save ${bulkEditRows.length} rooms`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: '1px solid rgba(244,67,54,0.25)',
              background: 'linear-gradient(135deg,#161616,#1e1e1e)',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            borderBottom: '1px solid rgba(244,67,54,0.15)',
            fontSize: '1rem',
            fontWeight: 700,
            color: '#f44336',
          }}
        >
          Delete Production Record
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            Delete the record for{' '}
            <strong>
              {deletingRecord
                ? new Date(deletingRecord.date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : ''}
            </strong>
            ? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2, borderTop: '1px solid rgba(244,67,54,0.12)' }}>
          <Button
            size="small"
            onClick={handleDeleteDialogClose}
            sx={{ color: 'rgba(255,255,255,0.5)' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            sx={{
              background: 'linear-gradient(135deg,#f44336,#ef5350)',
              color: '#fff',
              px: 2.5,
              '&:hover': { background: 'linear-gradient(135deg,#d32f2f,#f44336)' },
              '&:disabled': { background: 'rgba(244,67,54,0.25)' },
            }}
          >
            {deleteMutation.isPending ? <CircularProgress size={16} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BirdEggProductionPage;
